import React, { useEffect, useState } from 'react';
import { getEntityMetricsApi } from '../../api/SearchRequest';
import './Dashboard.css';

const MetricsModal = ({ result, resultType, onClose }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        const fetchMetrics = async () => {
            setLoading(true);
            try {
                const res = await getEntityMetricsApi(resultType, result._id);
                setMetrics(res.data);
            } catch (error) {
                console.error('Error fetching metrics:', error);
            }
            setLoading(false);
        };
        fetchMetrics();
    }, [result._id, resultType]);

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    };

    const formatDate = (ts) => new Date(ts).toLocaleString([], {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    const renderStatsBar = () => {
        if (resultType === 'item') return (
            <div className="metrics-stats-bar">
                <div className="stat-pill">👁 {metrics?.views || 0} {metrics?.viewLabel || 'views'}</div>
                <div className="stat-pill">🔗 {result.links?.length || 0} links</div>
                <div className="stat-pill">👤 {result.adoptions?.length || 0} adoptions</div>
            </div>
        );

        if (resultType === 'datum') return (
            <div className="metrics-stats-bar">
                <div className="stat-pill">👁 {metrics?.views || 0} views</div>
                <div className="stat-pill">👁 {metrics?.currentViews || 0} viewed</div>
                <div className="stat-pill">🔖 {metrics?.currentStamps || 0} stamps</div>
                <div className="stat-pill">💬 {metrics?.feedback || 0} feedback</div>
            </div>
        );

        if (resultType === 'user') return (
            <div className="metrics-stats-bar">
                <div className="stat-pill">🔐 {metrics?.logins || 0} logins</div>
                <div className="stat-pill">📊 {metrics?.sessions || 0} sessions</div>
                <div className="stat-pill">⏱ {formatDuration(metrics?.avgSessionLength)} avg</div>
                <div className="stat-pill">👁 {metrics?.itemViews || 0} item views</div>
                <div className="stat-pill">📄 {metrics?.datumViews || 0} datum views</div>
            </div>
        );
    };

    const renderEntityHeader = () => {
        if (resultType === 'item') return (
            <div className="metrics-entity-header">
                {result.image && (
                    <img
                        src={process.env.REACT_APP_PUBLIC_FOLDER + result.image}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                    />
                )}
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{result.itemName}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Item · Type {result.itemType}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{result._id}</div>
                </div>
            </div>
        );

        if (resultType === 'datum') return (
            <div className="metrics-entity-header">
                <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {result.datumText
                            ? result.datumText.slice(0, 120) + (result.datumText.length > 120 ? '...' : '')
                            : '(No text — relation datum)'
                        }
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Datum · by {result.userId}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{result._id}</div>
                </div>
            </div>
        );

        if (resultType === 'user') return (
            <div className="metrics-entity-header">
                {result.userImage && (
                    <img
                        src={process.env.REACT_APP_PUBLIC_FOLDER + result.userImage}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '50%' }}
                    />
                )}
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>@{result.username}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                        {result.userType === 0 ? 'General User' : result.userType === 1 ? 'Author' : 'Group'}
                        {result.displayname && ` · ${result.displayname}`}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{result._id}</div>
                </div>
            </div>
        );
    };

    const renderLogStream = (logs, label) => (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: '#374151' }}>
                {label}
            </div>
            {logs && logs.length > 0 ? logs.map((log, i) => (
                <div key={i} className="compact-log-item" style={{ fontSize: 11 }}>
                    <div className="log-user">
                        <span>{log.data?.username || log.userId || '@system'}</span>
                        <span className="log-activity"> — {log.activity}</span>
                        {log.status && (
                            <span style={{ color: log.status === 'success' ? '#16a34a' : '#ef4444', marginLeft: 4 }}>
                                [{log.status}]
                            </span>
                        )}
                    </div>
                    <div className="timestamp">{formatDate(log.createdAt)}</div>
                </div>
            )) : (
                <div style={{ fontSize: 11, color: '#9ca3af' }}>No activity found</div>
            )}
        </div>
    );

    const renderContent = () => {
        if (loading) return (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                Loading metrics...
            </div>
        );

        if (resultType === 'user') return (
            <>
                <div className="metrics-tab-bar">
                    <button
                        className={`metrics-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button
                        className={`metrics-tab ${activeTab === 'activity_by' ? 'active' : ''}`}
                        onClick={() => setActiveTab('activity_by')}
                    >
                        Activity By User
                    </button>
                    <button
                        className={`metrics-tab ${activeTab === 'activity_on' ? 'active' : ''}`}
                        onClick={() => setActiveTab('activity_on')}
                    >
                        Activity On User
                    </button>
                </div>

                {activeTab === 'overview' && (
                    <div style={{ padding: '0 4px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                            <div className="metrics-kv-box">
                                <span className="metrics-kv-label">Total Logins</span>
                                <span className="metrics-kv-value">{metrics?.logins || 0}</span>
                            </div>
                            <div className="metrics-kv-box">
                                <span className="metrics-kv-label">Total Sessions</span>
                                <span className="metrics-kv-value">{metrics?.sessions || 0}</span>
                            </div>
                            <div className="metrics-kv-box">
                                <span className="metrics-kv-label">Avg Session</span>
                                <span className="metrics-kv-value">{formatDuration(metrics?.avgSessionLength)}</span>
                            </div>
                            <div className="metrics-kv-box">
                                <span className="metrics-kv-label">Item Views</span>
                                <span className="metrics-kv-value">{metrics?.itemViews || 0}</span>
                            </div>
                            <div className="metrics-kv-box">
                                <span className="metrics-kv-label">Datum Views</span>
                                <span className="metrics-kv-value">{metrics?.datumViews || 0}</span>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 'activity_by' && renderLogStream(metrics?.recentLogsBy, 'Actions performed by this user')}
                {activeTab === 'activity_on' && renderLogStream(metrics?.actionsOnUser, 'Actions performed on this user')}
            </>
        );

        if (resultType === 'item') return (
            <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Total Views</span>
                        <span className="metrics-kv-value">{metrics?.views || 0}</span>
                    </div>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Links</span>
                        <span className="metrics-kv-value">{result.links?.length || 0}</span>
                    </div>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Adoptions</span>
                        <span className="metrics-kv-value">{result.adoptions?.length || 0}</span>
                    </div>
                </div>
                {renderLogStream(metrics?.recentLogs, 'Recent activity on this item')}
            </>
        );

        if (resultType === 'datum') return (
            <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Total Views</span>
                        <span className="metrics-kv-value">{metrics?.views || 0}</span>
                    </div>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Current Stamps</span>
                        <span className="metrics-kv-value">{metrics?.currentStamps || 0}</span>
                    </div>
                    <div className="metrics-kv-box">
                        <span className="metrics-kv-label">Feedback Count</span>
                        <span className="metrics-kv-value">{metrics?.feedback || 0}</span>
                    </div>
                </div>
                {renderLogStream(metrics?.recentLogs, 'Recent activity on this datum')}
            </>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="metrics-modal"
                onClick={e => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 10,
                    padding: 20,
                    width: '90%',
                    maxWidth: 680,
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                        {resultType} metrics
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}
                    >
                        ✕
                    </button>
                </div>

                {renderEntityHeader()}
                <hr style={{ margin: '12px 0' }} />
                {renderStatsBar()}
                <hr style={{ margin: '12px 0' }} />
                {renderContent()}
            </div>
        </div>
    );
};

export default MetricsModal;